import React, { useCallback, useMemo, useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { ToastManager } from '@features';
import { NewFeatureChip, TriggerBtn } from '@features/Checklist/ui';
import { Flex, Icon, Popup, UiButton, UiText } from '@shared/ui';

import cls from './Checklist.module.css';
import { getChecklistToastScope } from './lib/helpers';
import { ChecklistContext } from './model/context';
import {
    useChecklistController,
    useChecklistDnd,
    useSyncChecklistBadge,
    useSyncChecklistOpen,
    useNewFeatureChip,
} from './model/hooks';
import { ChecklistHeader, ChecklistItem, ChecklistSortable } from './ui';

interface IChecklistProps {
    positionGuid?: string;
    onOpenChange?: (isOpen: boolean) => void;
}
export const Checklist = ({ positionGuid = '', onOpenChange }: IChecklistProps) => {
    const { t } = useTranslation();
    const [toastPortalTarget, setToastPortalTarget] = useState<HTMLDivElement | null>(null);
    const toastScope = useMemo(() => getChecklistToastScope(positionGuid), [positionGuid]);
    const {
        isOpenChecklist,
        isOpenDeletePopup,
        rawChecklist,
        persistedChecklist,
        isChecklistHydrated,
        isCopied,
        isEditMode,
        disableToUpdate,
        isUpdateChecklistPending,
        isPendingData,
        hideTriggerBtn,
        isChecklistDataError,
        lastAddedChecklistItemClientId,
        canModifyChecklist,
        onAddChecklistItem,
        onDeleteChecklistItem,
        onReorderChecklistItem,
        onChangeChecklistItemText,
        onToggleChecklistItem,
        openDeletePopup,
        closeDeletePopup,
        handleClick: handleChecklistTriggerClick,
        handleDelete,
        handleCopy,
        handlePasteButtonClick,
        handleUpdateChecklistData,
        enableEditMode,
        disableEditMode,
    } = useChecklistController(positionGuid);

    const { enableNewFeatureChip, handleCloseNewFeatureChip } = useNewFeatureChip();

    useSyncChecklistBadge({
        positionGuid,
        checklist:
            isPendingData || !isChecklistHydrated
                ? null
                : isEditMode
                ? persistedChecklist
                : rawChecklist,
        labelPrefix: t('Checklist.Badge.label.text'),
    });
    useSyncChecklistOpen({ isOpen: isOpenChecklist, onOpenChange });

    const orderedChecklist = useMemo(
        () => [...rawChecklist].sort((a, b) => a.sortOrder - b.sortOrder),
        [rawChecklist]
    );
    const checklistCounter = useMemo(
        () => rawChecklist.filter((item) => item.text.trim().length > 0).length,
        [rawChecklist]
    );

    const handleChecklistContainerRef = useCallback((node: HTMLDivElement | null) => {
        setToastPortalTarget(node);
    }, []);

    const {
        sensors,
        checklistListRef,
        isDraggingChecklist,
        activeChecklistItem,
        restrictToChecklistBounds,
        onDragStart,
        onDragCancel,
        onDragEnd,
    } = useChecklistDnd({
        checklist: orderedChecklist,
        onReorderChecklistItem,
    });

    const checklistContextData = useMemo(
        () => ({
            rawChecklist: orderedChecklist,
            checklistCounter,
            isEditMode,
            isCopied,
            isDraggingChecklist,
            lastAddedChecklistItemClientId,
            canModifyChecklist,
            onDeleteChecklistItem,
            onAddChecklistItem,
            onReorderChecklistItem,
            onChangeChecklistItemText,
            onToggleChecklistItem,
            openDeletePopup,
            onEditModeHandler: enableEditMode,
            onCopyHandler: handleCopy,
            onDeleteHandler: handleDelete,
        }),
        [
            openDeletePopup,
            onDeleteChecklistItem,
            onAddChecklistItem,
            onReorderChecklistItem,
            onChangeChecklistItemText,
            onToggleChecklistItem,
            enableEditMode,
            handleCopy,
            handleDelete,
            canModifyChecklist,
            isCopied,
            isDraggingChecklist,
            isEditMode,
            lastAddedChecklistItemClientId,
            orderedChecklist,
            checklistCounter,
        ]
    );
    const shouldRenderTrigger = !isPendingData && !isChecklistDataError && !hideTriggerBtn;

    // временный фикс вывода чеклистов
    return (
        <>
            {shouldRenderTrigger && enableNewFeatureChip && (
                <NewFeatureChip onClose={handleCloseNewFeatureChip} />
            )}
            {shouldRenderTrigger && (
                <div
                    className={cn(
                        cls.ChecklistTrigger,
                        enableNewFeatureChip && cls.ChecklistTriggerAboveOverlay
                    )}
                >
                    <div
                        className={cn(
                            cls.ChecklistTriggerHighlight,
                            enableNewFeatureChip && cls.ChecklistTriggerHighlightActive
                        )}
                    >
                        <TriggerBtn
                            isOpenChecklist={isOpenChecklist}
                            triggerCb={handleChecklistTriggerClick}
                            checklistData={rawChecklist}
                            enableNewFeatureChip={enableNewFeatureChip}
                            isEditMode={isEditMode}
                        />
                    </div>
                </div>
            )}

            <ChecklistContext.Provider value={checklistContextData}>
                {isOpenChecklist && (
                    <Flex
                        ref={handleChecklistContainerRef}
                        direction="column"
                        align="start"
                        className={cn(
                            cls.ChecklistContainer,
                            isDraggingChecklist && cls.isDraggingChecklist
                        )}
                    >
                        <ChecklistHeader />

                        <Flex as="main" fullWidth align="start" className={cls.ChecklistMain}>
                            <ChecklistSortable
                                checklist={orderedChecklist}
                                activeChecklistItem={activeChecklistItem}
                                sensors={sensors}
                                modifiers={[restrictToChecklistBounds]}
                                onDragStart={onDragStart}
                                onDragCancel={onDragCancel}
                                onDragEnd={onDragEnd}
                            >
                                <Flex
                                    ref={checklistListRef}
                                    direction="column"
                                    align="start"
                                    as="ul"
                                    className={cn(cls.ChecklistUl)}
                                    fullWidth
                                >
                                    {orderedChecklist.map((item) => (
                                        <ChecklistItem
                                            key={item.clientId}
                                            itemData={item}
                                            canEdit={isEditMode}
                                        />
                                    ))}
                                </Flex>
                            </ChecklistSortable>
                        </Flex>
                        {isEditMode && (
                            <Flex className={cls.EditControlsWrapper}>
                                <UiButton
                                    onClick={() => onAddChecklistItem()}
                                    variant="text"
                                    disabled={!isEditMode}
                                    btnText={t('Checklist.AddItem.text')}
                                    btnTextStyles={cls.EditModeBtnText}
                                    className={cls.EditModeBtn}
                                />
                                <UiText className={cls.TertiarySpan}>
                                    {t('Checklist.AddCopiedChecklist.Or.text')}
                                </UiText>
                                <UiButton
                                    variant="text"
                                    btnText={t('Checklist.AddCopiedChecklist.text')}
                                    onClick={handlePasteButtonClick}
                                />
                            </Flex>
                        )}

                        {isEditMode && (
                            <Flex
                                as="footer"
                                justify="between"
                                className={cls.ChecklistFooter}
                                fullWidth
                            >
                                <UiButton
                                    btnColor="secondary"
                                    btnText={t('button.cancel.text')}
                                    onClick={disableEditMode}
                                />
                                <UiButton
                                    btnText={t('button.save.text')}
                                    onClick={handleUpdateChecklistData}
                                    disabled={disableToUpdate || isUpdateChecklistPending}
                                />
                            </Flex>
                        )}
                    </Flex>
                )}
                <Popup
                    isOpen={isOpenDeletePopup}
                    type="delete"
                    title={t('Checklist.DeleteItem.Popup.Header.text')}
                    contentText={t('Checklist.DeleteItem.Popup.Content.text')}
                    popupContentClassName={cls.PopupContent}
                    headerClassName={cls.PopupHeader}
                    footerClassName={cls.PopupFooter}
                    btnActionCb={handleDelete}
                    onHidePopup={closeDeletePopup}
                />
            </ChecklistContext.Provider>
            {toastPortalTarget && (
                <ToastManager
                    toastPosition="right"
                    strategy="absolute"
                    portalTarget={toastPortalTarget}
                    delay={1000}
                    scope={toastScope}
                    renderToast={({ isToastShow, positionStyles, text, type }) => (
                        <div
                            className={cn(cls.CustomToastContainer, isToastShow && cls.isToastOpen)}
                            style={positionStyles}
                        >
                            {type === 'success' && <Icon iconName="isChecked" />}
                            {type === 'error' && <Icon iconName="cross" fill="#EC3030" />}
                            <span className={cls.CustomToastContent}>{text}</span>
                        </div>
                    )}
                />
            )}
        </>
    );
};
